export type UploadDeviceInfo = {
  uploadDeviceModel?: string;
  uploadDevicePlatform?: string;
  uploadDevicePlatformVersion?: string;
};

let cached: Promise<UploadDeviceInfo> | null = null;

function safeTrim(value: unknown, maxLen: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length > maxLen ? trimmed.slice(0, maxLen) : trimmed;
}

function inferModelFromUserAgent(userAgent: string): string | undefined {
  const ua = userAgent || "";
  if (!ua) return undefined;

  if (ua.includes("iPhone")) return "iPhone";
  if (ua.includes("iPad")) return "iPad";
  if (ua.includes("Macintosh")) return "Mac";
  if (ua.includes("Windows")) return "Windows PC";

  // Android UA often contains: "... Android 14; Pixel 8 Pro Build/..."
  const androidMatch = ua.match(/Android [^;]+;\s*([^;]+)\s*Build\//i);
  if (androidMatch && androidMatch[1]) return androidMatch[1].trim();

  if (ua.includes("Android")) return "Android device";
  if (ua.includes("Linux")) return "Linux PC";

  return undefined;
}

/**
 * Best-effort client device info for uploads.
 *
 * Notes:
 * - Browsers do NOT expose true device serial numbers for privacy/security reasons.
 * - `navigator.userAgentData` (UA-CH) can provide a "model" on some Chromium-based Android browsers.
 *   On iOS Safari this will generally be absent.
 */
export async function getUploadDeviceInfo(): Promise<UploadDeviceInfo> {
  if (cached) return await cached;

  cached = (async (): Promise<UploadDeviceInfo> => {
    const nav = navigator as unknown as {
      userAgent?: string;
      platform?: string;
      userAgentData?: {
        getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
        platform?: string;
      };
    };

    let uploadDeviceModel: string | undefined;
    let uploadDevicePlatform: string | undefined;
    let uploadDevicePlatformVersion: string | undefined;

    try {
      const uad = nav.userAgentData;
      if (uad?.getHighEntropyValues) {
        const values = await uad.getHighEntropyValues([
          "model",
          "platform",
          "platformVersion",
        ]);
        uploadDeviceModel = safeTrim(values?.model, 200);
        uploadDevicePlatform = safeTrim(values?.platform, 100) || safeTrim(uad.platform, 100);
        uploadDevicePlatformVersion = safeTrim(values?.platformVersion, 100);
      }
    } catch {
      // ignore UA-CH failures
    }

    const ua = safeTrim(nav.userAgent, 500) || "";
    uploadDeviceModel = uploadDeviceModel || inferModelFromUserAgent(ua);
    uploadDevicePlatform =
      uploadDevicePlatform || safeTrim(nav.platform, 100) || inferModelFromUserAgent(ua);

    return {
      uploadDeviceModel,
      uploadDevicePlatform,
      uploadDevicePlatformVersion,
    };
  })();

  return await cached;
}

